import { ChevronDown } from 'lucide-react';
import React from 'react';
import { Button } from '@components/ui/button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import { cn } from '@core/utils/cn';
import { Option, Select } from '../select';
import { FieldError, FieldLabel } from '../textfield';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface BaseFiltersProps<T = any> {
    label: string;
    required?: boolean;
    error?: string;
    options: Option<T>[];
    multiple?: boolean;
    width?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface MultipleFiltersProps<T = any> extends BaseFiltersProps {
    value: T[];
    onChange(value: T[]): void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface SingleFiltersProps<T = any> extends BaseFiltersProps {
    value: T;
    onChange(value: T): void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FiltersProps<T = any, Multi = true> = Multi extends true
    ? MultipleFiltersProps<T>
    : SingleFiltersProps<T>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MultipleFilters = <T extends any>({
    label,
    required,
    value,
    error,
    options,
    onChange
}: MultipleFiltersProps<T>) => {
    const id = React.useId();
    const selectedLabels = options
        .filter((option) => value.includes(option.value))
        .map((option) => option.label);

    const toggle = (optionValue: T, checked: boolean) =>
        onChange(
            checked
                ? [...value, optionValue]
                : value.filter((item) => item !== optionValue)
        );

    return (
        <div className="flex w-full flex-col gap-1.5">
            <FieldLabel htmlFor={id} label={label} required={required} />
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        id={id}
                        type="button"
                        variant="outline"
                        aria-invalid={Boolean(error)}
                        className={cn(
                            'w-full justify-between font-normal',
                            selectedLabels.length === 0 &&
                                'text-muted-foreground'
                        )}
                    >
                        <span className="truncate">
                            {selectedLabels.length > 0
                                ? selectedLabels.join(', ')
                                : 'All'}
                        </span>
                        <ChevronDown className="text-muted-foreground" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    className="min-w-(--radix-dropdown-menu-trigger-width)"
                >
                    {options.map((option) => (
                        <DropdownMenuCheckboxItem
                            key={String(option.value)}
                            checked={value.includes(option.value)}
                            onCheckedChange={(checked) =>
                                toggle(option.value, checked === true)
                            }
                            onSelect={(event) => event.preventDefault()}
                        >
                            <span className="flex items-center gap-2">
                                {option.label}
                                {option.adornment}
                            </span>
                        </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <FieldError error={error} />
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Filters = <T extends any, Multi extends boolean>(
    props: FiltersProps<T, Multi>
) => {
    const { width, multiple } = props;
    const style = { flex: width ? `0 0 ${width}px` : 1 };

    if (multiple) {
        return (
            <div style={style}>
                <MultipleFilters {...(props as MultipleFiltersProps<T>)} />
            </div>
        );
    }

    const { label, required, value, error, options, onChange } =
        props as SingleFiltersProps<T>;
    return (
        <div style={style}>
            <Select
                label={label}
                required={required}
                value={value}
                error={error}
                options={options}
                onChange={onChange}
            />
        </div>
    );
};
